import React from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import useSurveys from "@/hooks/useSurveys";
import useTeams from "@/hooks/useTeams";
import useRules from "@/hooks/useRules";
import useActivityLog from "@/hooks/useActivityLog";
import useWizardState from "@/hooks/useWizardState";
import useWorkflows from "@/hooks/useWorkflows";
import Select from "@/components/Select";
import { Rule } from "./types";

type Scenario =
  | "survey"
  | "time-of-day"
  | "url"
  | "language"
  | "business-hours"
  | "geographic"
  | "unavailable-survey"
  | "unavailable-bot"
  | "overflow"
  | null;

interface GeoRow {
  country: string;
  teamId: number;
}

interface WizardData {
  scenario: Scenario;
  step: number;
  // Survey routing
  selectedSurveyId: number | null;
  selectedFieldVariable: string | null;
  optionTeamMap: Record<string, number>;
  // Business hours routing
  bhInHoursTeamId: number;
  bhAfterHoursTeamId: number;
  bhFromTime: string;
  bhToTime: string;
  bhDays: string[];
  // Geographic routing
  geoRows: GeoRow[];
  // Unavailability routing
  unavailSurveyId: number | null;
  unavailWorkflowId: number | null;
  // Overflow routing
  overflowSourceTeamId: number;
  overflowDestTeamId: number;
  overflowThreshold: number;
}

const INITIAL: WizardData = {
  scenario: null,
  step: 0,
  selectedSurveyId: null,
  selectedFieldVariable: null,
  optionTeamMap: {},
  bhInHoursTeamId: 0,
  bhAfterHoursTeamId: 0,
  bhFromTime: "09:00",
  bhToTime: "17:00",
  bhDays: ["mon", "tues", "wed", "thurs", "fri"],
  geoRows: [{ country: "", teamId: 0 }],
  unavailSurveyId: null,
  unavailWorkflowId: null,
  overflowSourceTeamId: 0,
  overflowDestTeamId: 0,
  overflowThreshold: 5
};

interface Props {
  close(): void;
  openManualEditor(preset: Partial<Rule>): void;
}

// ─── Country list (matches MaxMind GeoIP2 names) ──────────────────────────────
const COUNTRIES: string[] = [
  "Argentina", "Australia", "Austria", "Bangladesh", "Belgium", "Brazil",
  "Canada", "Chile", "China", "Czech Republic", "Denmark", "Egypt",
  "Finland", "France", "Germany", "Greece", "Hong Kong", "India",
  "Indonesia", "Ireland", "Israel", "Italy", "Japan", "Kenya",
  "Malaysia", "Mexico", "Netherlands", "New Zealand", "Nigeria", "Norway",
  "Pakistan", "Philippines", "Poland", "Portugal", "Russia", "Saudi Arabia",
  "Singapore", "South Africa", "South Korea", "Spain", "Sweden", "Switzerland",
  "Taiwan", "Thailand", "Turkey", "Ukraine", "United Arab Emirates",
  "United Kingdom", "United States", "Vietnam"
];

// ─── Day-of-week helpers ──────────────────────────────────────────────────────
const DAY_KEYS = ["mon", "tues", "wed", "thurs", "fri", "sat", "sun"] as const;
const DAY_LABELS: Record<string, string> = {
  mon: "Mon", tues: "Tue", wed: "Wed", thurs: "Thu",
  fri: "Fri", sat: "Sat", sun: "Sun"
};

// ─── Scenario cards ───────────────────────────────────────────────────────────
const SCENARIOS: { id: Scenario; icon: string; title: string; description: string }[] = [
  {
    id: "survey",
    icon: "📋",
    title: "Route by survey answer",
    description:
      'Visitor picks "Sales" → goes to Sales team. One setup creates all the routing rules automatically.'
  },
  {
    id: "language",
    icon: "🌐",
    title: "Route by visitor language",
    description:
      "Spanish speakers → Spanish team. French speakers → French team. Route by browser language automatically."
  },
  {
    id: "business-hours",
    icon: "🕐",
    title: "Business hours routing",
    description:
      "Route visitors to your team during business hours. After hours, route to a different team or fallback."
  },
  {
    id: "geographic",
    icon: "🌍",
    title: "Route by country",
    description:
      "US visitors → US team. UK visitors → UK team. Route automatically by visitor location."
  },
  {
    id: "url",
    icon: "🔗",
    title: "Route by page URL",
    description: "Visitors on /pricing go to Sales. Visitors on /support go to the Support team."
  },
  {
    id: "unavailable-survey",
    icon: "📴",
    title: "No agents? Show a survey",
    description:
      "When no agents are available, automatically show an unavailability survey to capture contact info."
  },
  {
    id: "unavailable-bot",
    icon: "🤖",
    title: "No agents? Launch a bot",
    description:
      "When no agents are available, run a workflow or bot to greet visitors and collect details before routing."
  },
  {
    id: "overflow",
    icon: "🔀",
    title: "Overflow when queue is full",
    description:
      "When one team's queue exceeds a threshold (e.g. 5 waiting), automatically route new conversations to a backup team."
  }
];

export default function QuickRuleSetupModal({ close, openManualEditor }: Props) {
  const { surveys } = useSurveys();
  const { teams } = useTeams();
  const { add, refresh } = useRules();
  const { track } = useActivityLog();
  const { workflows } = useWorkflows();

  const {
    wizardState: ws,
    setWizardState,
    clearWizardState,
    isResuming,
    resumedFromLabel,
    resumeWizard,
    startFresh
  } = useWizardState<WizardData>("routing-wizard", INITIAL);

  const set = <K extends keyof WizardData>(key: K, value: WizardData[K]) =>
    setWizardState((prev) => ({ ...prev, [key]: value }));

  const savedSurveys = surveys.filter((s) => !!s.id);
  const selectedSurvey = savedSurveys.find((s) => s.id === ws.selectedSurveyId);
  const choiceFields = (selectedSurvey?.questions ?? []).filter(
    (q) => (q.type === "choice" || q.type === "checkbox") && q.variable
  );
  const selectedField = choiceFields.find((f) => f.variable === ws.selectedFieldVariable) ?? null;
  const choiceOptions = (selectedField?.options ?? []).filter((o) => o.trim());

  const teamList = Array.isArray(teams) ? teams : [];
  const defaultTeam = teamList.find((t) => t.isDefault) ?? teamList[0] ?? null;

  const teamOptions = teamList.reduce(
    (acc, t) => { acc[t.id.toString()] = t.name; return acc; },
    {} as Record<string, string>
  );

  const mappedCount = Object.values(ws.optionTeamMap).filter((id) => id > 0).length;
  const canSaveSurveyRouting = selectedField && choiceOptions.length > 0 && mappedCount > 0;

  // ─── Business hours helpers ──────────────────────────────────────────────────

  const canSaveBH =
    ws.bhInHoursTeamId > 0 &&
    ws.bhFromTime &&
    ws.bhToTime &&
    ws.bhDays.length > 0;

  function toggleBhDay(day: string) {
    const next = ws.bhDays.includes(day)
      ? ws.bhDays.filter((d) => d !== day)
      : [...ws.bhDays, day];
    set("bhDays", next);
  }

  // ─── Geographic helpers ───────────────────────────────────────────────────────

  const validGeoRows = ws.geoRows.filter((r) => r.country && r.teamId > 0);
  const canSaveGeo = validGeoRows.length > 0;

  function updateGeoRow(index: number, patch: Partial<GeoRow>) {
    const rows = ws.geoRows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    set("geoRows", rows);
  }

  function addGeoRow() {
    set("geoRows", [...ws.geoRows, { country: "", teamId: 0 }]);
  }

  function removeGeoRow(index: number) {
    set("geoRows", ws.geoRows.filter((_, i) => i !== index));
  }

  // ─── Save functions ───────────────────────────────────────────────────────────

  async function saveSurveyRouting() {
    if (!selectedField) return;
    const entries = Object.entries(ws.optionTeamMap).filter(([, teamId]) => teamId > 0);
    let priority = 1;
    for (const [option, teamId] of entries) {
      await add({
        trigger: "route-to-team",
        priority: priority++,
        value: { teamId },
        expression: [
          [
            {
              type: "attribute",
              data: JSON.stringify({
                attribute: selectedField.variable,
                comparator: "equals",
                value: option
              })
            }
          ]
        ]
      } as Rule);
    }
    track({
      category: "Rules",
      action: "WizardCreatedRules",
      entityType: "Rule",
      entityName: `Routing Wizard — survey (${entries.length} rules)`
    });
    clearWizardState();
    refresh();
    close();
  }

  async function saveBusinessHoursRouting() {
    // In-hours rule: timeOfDay AND dayOfWeek conditions
    const inHoursExpression: Rule["expression"][0] = [
      { type: "timeOfDay", data: JSON.stringify({ from: ws.bhFromTime, to: ws.bhToTime }) }
    ];
    if (ws.bhDays.length > 0) {
      inHoursExpression.push({
        type: "dayOfWeek",
        data: JSON.stringify({ daysOfWeek: ws.bhDays })
      });
    }
    await add({
      trigger: "route-to-team",
      priority: 1,
      value: { teamId: ws.bhInHoursTeamId },
      expression: [inHoursExpression]
    } as Rule);

    // After-hours catch-all (no conditions)
    if (ws.bhAfterHoursTeamId > 0) {
      await add({
        trigger: "route-to-team",
        priority: 2,
        value: { teamId: ws.bhAfterHoursTeamId },
        expression: []
      } as Rule);
    }

    track({
      category: "Rules",
      action: "WizardCreatedRules",
      entityType: "Rule",
      entityName: `Routing Wizard — business hours`
    });
    clearWizardState();
    refresh();
    close();
  }

  async function saveGeographicRouting() {
    let priority = 1;
    for (const row of validGeoRows) {
      await add({
        trigger: "route-to-team",
        priority: priority++,
        value: { teamId: row.teamId },
        expression: [
          [
            {
              type: "country",
              data: JSON.stringify({ country: row.country, comparator: "equals" })
            }
          ]
        ]
      } as Rule);
    }
    track({
      category: "Rules",
      action: "WizardCreatedRules",
      entityType: "Rule",
      entityName: `Routing Wizard — geographic (${validGeoRows.length} rules)`
    });
    clearWizardState();
    refresh();
    close();
  }

  // ─── Unavailable survey save ──────────────────────────────────────────────────

  async function saveUnavailableSurvey() {
    if (!ws.unavailSurveyId) return;
    await add({
      trigger: "use-unavailable-survey",
      priority: 1,
      value: { surveyId: ws.unavailSurveyId },
      expression: []
    } as Rule);
    track({
      category: "Rules",
      action: "WizardCreatedRules",
      entityType: "Rule",
      entityName: "Routing Wizard — unavailable survey"
    });
    clearWizardState();
    refresh();
    close();
  }

  async function saveUnavailableBot() {
    if (!ws.unavailWorkflowId) return;
    await add({
      trigger: "use-workflow-on-unavailable",
      priority: 1,
      value: { workflowId: ws.unavailWorkflowId },
      expression: []
    } as Rule);
    track({
      category: "Rules",
      action: "WizardCreatedRules",
      entityType: "Rule",
      entityName: "Routing Wizard — unavailable bot/workflow"
    });
    clearWizardState();
    refresh();
    close();
  }

  // ─── Unavailable survey — single step ────────────────────────────────────────

  function renderUnavailableSurvey() {
    const surveySelectOptions = savedSurveys.reduce(
      (acc, s) => { acc[s.id!.toString()] = s.name; return acc; },
      {} as Record<string, string>
    );
    const selectedName = savedSurveys.find((s) => s.id === ws.unavailSurveyId)?.name;

    return (
      <>
        <ModalHeader title="Show survey when unavailable" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Pick the survey to show visitors when no agents are available. This
            creates a <span className="font-medium">Use Unavailable Survey</span> rule
            with no conditions — it fires whenever agents are offline.
          </p>

          {savedSurveys.length === 0 ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
              <p className="font-semibold mb-1">No surveys found.</p>
              <p>
                Create an unavailability survey first — your progress is saved.{" "}
                <a
                  href="/surveys"
                  className="underline font-medium hover:text-amber-900"
                  onClick={handleClose}
                >
                  Go to Surveys →
                </a>{" "}
                then come back and click{" "}
                <span className="font-medium">⚡ Quick Setup</span> again.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Survey to display
                </label>
                <Select
                  options={{ "": "— pick a survey —", ...surveySelectOptions }}
                  value={ws.unavailSurveyId?.toString() ?? ""}
                  onChange={(v) => set("unavailSurveyId", v ? Number(v) : null)}
                />
              </div>

              {selectedName && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                  <p className="font-medium text-slate-700 mb-1">What will be created:</p>
                  <div className="flex items-start gap-2">
                    <span className="inline-block mt-0.5 w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center shrink-0">1</span>
                    <p className="text-slate-700">
                      When agents are unavailable →{" "}
                      <span className="font-medium">show "{selectedName}"</span>
                    </p>
                  </div>
                </div>
              )}

              <p className="text-xs text-slate-400">
                You can add conditions (time of day, language, etc.) to this rule
                afterward in the rule editor to show different surveys in different situations.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Back" onClick={() => set("scenario", null)} />
            <PrimaryBlueButton
              label="Save Rule"
              disabled={!ws.unavailSurveyId}
              onClick={saveUnavailableSurvey}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Unavailable bot — single step ───────────────────────────────────────────

  function renderUnavailableBot() {
    const workflowList = Array.isArray(workflows) ? workflows : [];
    const workflowSelectOptions = workflowList.reduce(
      (acc, w) => { acc[w.id.toString()] = w.name; return acc; },
      {} as Record<string, string>
    );
    const selectedWorkflowName = workflowList.find((w) => w.id === ws.unavailWorkflowId)?.name;

    return (
      <>
        <ModalHeader title="Launch bot when unavailable" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Pick the workflow to run when no agents are available. This creates a{" "}
            <span className="font-medium">Use Workflow on Unavailable</span> rule.
            The workflow can greet the visitor, collect contact info, and offer a
            callback — all before routing resumes.
          </p>

          {workflowList.length === 0 ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
              <p className="font-semibold mb-1">No workflows found.</p>
              <p>
                Create a workflow first — your progress is saved.{" "}
                <a
                  href="/workflows"
                  className="underline font-medium hover:text-amber-900"
                  onClick={handleClose}
                >
                  Go to Workflows →
                </a>{" "}
                then come back and click{" "}
                <span className="font-medium">⚡ Quick Setup</span> again.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Workflow to run
                </label>
                <Select
                  options={{ "": "— pick a workflow —", ...workflowSelectOptions }}
                  value={ws.unavailWorkflowId?.toString() ?? ""}
                  onChange={(v) => set("unavailWorkflowId", v ? Number(v) : null)}
                />
              </div>

              {selectedWorkflowName && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                  <p className="font-medium text-slate-700 mb-1">What will be created:</p>
                  <div className="flex items-start gap-2">
                    <span className="inline-block mt-0.5 w-5 h-5 rounded-full bg-primary-bg text-primary-active text-xs font-bold flex items-center justify-center shrink-0">1</span>
                    <p className="text-slate-700">
                      When agents are unavailable →{" "}
                      <span className="font-medium">run "{selectedWorkflowName}"</span>
                    </p>
                  </div>
                </div>
              )}

              <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700">
                <span className="font-semibold">Tip:</span> Design your workflow to
                ask for the visitor's name and email, then use a{" "}
                <span className="font-medium">Transfer to Agents</span> node so
                agents can pick up when they come online.
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Back" onClick={() => set("scenario", null)} />
            <PrimaryBlueButton
              label="Save Rule"
              disabled={!ws.unavailWorkflowId}
              onClick={saveUnavailableBot}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Overflow routing save + render ──────────────────────────────────────────

  const canSaveOverflow =
    ws.overflowSourceTeamId > 0 &&
    ws.overflowDestTeamId > 0 &&
    ws.overflowSourceTeamId !== ws.overflowDestTeamId &&
    ws.overflowThreshold > 0;

  async function saveOverflowRouting() {
    await add({
      trigger: "route-to-team",
      priority: 1,
      value: { teamId: ws.overflowDestTeamId },
      expression: [
        [
          {
            type: "teamQueueSize",
            data: JSON.stringify({
              teamId: ws.overflowSourceTeamId,
              threshold: ws.overflowThreshold,
              comparator: "greaterThan"
            })
          }
        ]
      ]
    } as Rule);
    track({
      category: "Rules",
      action: "WizardCreatedRules",
      entityType: "Rule",
      entityName: `Routing Wizard — overflow (threshold ${ws.overflowThreshold})`
    });
    clearWizardState();
    refresh();
    close();
  }

  function renderOverflowStep() {
    const sourceTeam = teamList.find((t) => t.id === ws.overflowSourceTeamId);
    const destTeam = teamList.find((t) => t.id === ws.overflowDestTeamId);
    const destOptions = teamList.filter((t) => t.id !== ws.overflowSourceTeamId);
    const destSelectOptions = destOptions.reduce(
      (acc, t) => { acc[t.id.toString()] = t.name; return acc; },
      {} as Record<string, string>
    );

    return (
      <>
        <ModalHeader title="Overflow routing" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            When the primary team's queue fills up, new conversations automatically
            route to a backup team. Great for peak hours and SLA protection.
          </p>

          <div className="flex flex-col gap-4">
            {/* Primary team */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Primary team (the one that gets busy)
              </label>
              <Select
                options={{ "": "— pick primary team —", ...teamOptions }}
                value={ws.overflowSourceTeamId > 0 ? ws.overflowSourceTeamId.toString() : ""}
                onChange={(v) =>
                  setWizardState((prev) => ({
                    ...prev,
                    overflowSourceTeamId: Number(v),
                    overflowDestTeamId:
                      prev.overflowDestTeamId === Number(v) ? 0 : prev.overflowDestTeamId
                  }))
                }
              />
            </div>

            {/* Threshold */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Overflow when queue exceeds
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={999}
                  value={ws.overflowThreshold}
                  onChange={(e) => set("overflowThreshold", Math.max(1, Number(e.target.value)))}
                  className="w-20 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300"
                  aria-label="Overflow threshold"
                />
                <span className="text-sm text-slate-500">conversations waiting</span>
              </div>
            </div>

            {/* Destination team */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Overflow to
              </label>
              <Select
                options={{ "": "— pick overflow team —", ...destSelectOptions }}
                value={ws.overflowDestTeamId > 0 ? ws.overflowDestTeamId.toString() : ""}
                onChange={(v) => set("overflowDestTeamId", Number(v))}
              />
            </div>

            {/* Summary */}
            {canSaveOverflow && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                <p className="font-medium text-slate-700 mb-1">What will be created:</p>
                <div className="flex items-start gap-2">
                  <span className="inline-block mt-0.5 w-5 h-5 rounded-full bg-primary-bg text-primary-active text-xs font-bold flex items-center justify-center shrink-0">1</span>
                  <p className="text-slate-700">
                    When{" "}
                    <span className="font-medium">{sourceTeam?.name}</span>{" "}
                    has more than{" "}
                    <span className="font-medium">{ws.overflowThreshold}</span>{" "}
                    conversations waiting →{" "}
                    route new conversations to{" "}
                    <span className="font-medium">{destTeam?.name}</span>
                  </p>
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400">
              You can add more conditions (time of day, channel) in the rule editor afterward
              to limit when overflow kicks in.
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Back" onClick={() => set("scenario", null)} />
            <PrimaryBlueButton
              label="Save Rule"
              disabled={!canSaveOverflow}
              onClick={saveOverflowRouting}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  function handleClose() {
    close();
  }

  function handleCancel() {
    clearWizardState();
    close();
  }

  // ─── Resume prompt ────────────────────────────────────────────────────────────

  if (isResuming) {
    return (
      <Modal maxWidth="max-w-lg" show>
        <ModalHeader title="Quick Rule Setup" onClose={handleClose} />
        <ModalBody>
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-cornflower-blue-200 bg-primary-bg p-4">
              <p className="text-sm font-semibold text-cornflower-blue-800 mb-1">
                You left this wizard unfinished
              </p>
              <p className="text-sm text-primary-active">
                You started a routing wizard{" "}
                <span className="font-medium">{resumedFromLabel}</span>.
                Pick up where you left off, or start a new one.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <PrimaryBlueButton
                label="Continue where I left off"
                onClick={resumeWizard}
              />
              <SecondaryBlueButton
                label="Start fresh"
                onClick={startFresh}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="px-4 py-3 w-full">
            <button
              type="button"
              className="text-sm text-slate-400 hover:text-slate-600"
              onClick={handleCancel}
            >
              Cancel — discard saved progress
            </button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }

  // ─── Scenario picker ──────────────────────────────────────────────────────────

  function renderPicker() {
    return (
      <>
        <ModalHeader title="Quick Rule Setup" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Pick a common scenario and we'll guide you through it — or open
            the full editor for complete control.
          </p>

          {teamList.length === 0 && (
            <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              <span className="font-semibold">You don't have any teams yet.</span>{" "}
              Create at least one team first — your progress here will be saved
              automatically.{" "}
              <a
                href="/teams"
                className="underline font-medium hover:text-amber-900"
                onClick={handleClose}
              >
                Go to Teams →
              </a>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {SCENARIOS.map((s) => (
              <button
                key={s.id}
                type="button"
                disabled={teamList.length === 0}
                className="flex items-start gap-3 rounded-lg border border-slate-200 p-4 text-left hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                onClick={() => handleScenarioSelect(s.id)}
              >
                <span className="text-2xl leading-none">{s.icon}</span>
                <div>
                  <p className="font-medium text-slate-800 text-sm">{s.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.description}</p>
                </div>
              </button>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Cancel" onClick={handleCancel} />
            <button
              type="button"
              className="text-sm text-slate-500 hover:text-blue-600 hover:underline"
              onClick={() => {
                clearWizardState();
                openManualEditor({
                  trigger: "route-to-team",
                  priority: 1,
                  value: { teamId: defaultTeam?.id ?? 0 },
                  expression: []
                });
              }}
            >
              Build manually →
            </button>
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Survey routing — Step 1: pick survey + field ─────────────────────────────

  function renderSurveyStep0() {
    const surveySelectOptions = savedSurveys.reduce(
      (acc, s) => { acc[s.id!.toString()] = s.name; return acc; },
      {} as Record<string, string>
    );

    const fieldSelectOptions = choiceFields.reduce(
      (acc, q) => { acc[q.variable!] = q.text || q.variable!; return acc; },
      {} as Record<string, string>
    );

    return (
      <>
        <ModalHeader title="Route by survey answer — Step 1 of 2" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Choose which survey field to route by. Only choice and checkbox
            fields are shown because they have known options.
          </p>

          {savedSurveys.length === 0 ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
              <p className="font-semibold mb-1">No surveys found.</p>
              <p>
                Create a prechat survey first — your progress here is saved.{" "}
                <a
                  href="/surveys"
                  className="underline font-medium hover:text-amber-900"
                  onClick={handleClose}
                >
                  Go to Surveys →
                </a>
                {" "}then come back and click{" "}
                <span className="font-medium">⚡ Quick Setup</span> again to continue.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Survey
                </label>
                <Select
                  options={surveySelectOptions}
                  value={ws.selectedSurveyId?.toString() ?? ""}
                  onChange={(v) => {
                    setWizardState((prev) => ({
                      ...prev,
                      selectedSurveyId: Number(v),
                      selectedFieldVariable: null,
                      optionTeamMap: {}
                    }));
                  }}
                />
              </div>

              {ws.selectedSurveyId && choiceFields.length === 0 && (
                <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  This survey has no choice or checkbox fields. Add one in{" "}
                  <a href="/surveys" className="underline" onClick={handleClose}>
                    Surveys →
                  </a>{" "}
                  then come back — your progress is saved.
                </div>
              )}

              {choiceFields.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Field to route by
                  </label>
                  <Select
                    options={fieldSelectOptions}
                    value={ws.selectedFieldVariable ?? ""}
                    onChange={(v) => {
                      setWizardState((prev) => ({
                        ...prev,
                        selectedFieldVariable: v || null,
                        optionTeamMap: {}
                      }));
                    }}
                  />
                  {selectedField && (
                    <p className="mt-1 text-xs text-slate-400">
                      Variable:{" "}
                      <code className="font-mono text-blue-500">
                        {"<<"}{selectedField.variable}{">>"}
                      </code>
                    </p>
                  )}
                </div>
              )}

              {selectedField && choiceOptions.length > 0 && (
                <div className="rounded bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-xs text-slate-500 mb-1">Options in this field:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {choiceOptions.map((o) => (
                      <span
                        key={o}
                        className="text-xs bg-white border border-slate-300 rounded px-2 py-0.5 text-slate-700"
                      >
                        {o}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Back" onClick={() => set("scenario", null)} />
            <PrimaryBlueButton
              label="Next →"
              disabled={!selectedField || choiceOptions.length === 0}
              onClick={() => set("step", 1)}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Survey routing — Step 2: map options to teams ────────────────────────────

  function renderSurveyStep1() {
    const singleTeam = teamList.length === 1 ? teamList[0] : null;

    return (
      <>
        <ModalHeader title="Route by survey answer — Step 2 of 2" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-1">
            Choose which team each answer should route to. Skip options you
            don't want to route.
          </p>
          <p className="text-xs text-slate-400 mb-4">
            Field:{" "}
            <code className="font-mono text-blue-500">
              {"<<"}{selectedField?.variable}{">>"}
            </code>
          </p>

          {singleTeam && (
            <div className="mb-3 rounded bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700">
              You only have one team (<span className="font-semibold">{singleTeam.name}</span>).
              All answers will route there by default.
            </div>
          )}

          <div className="flex flex-col gap-2">
            {choiceOptions.map((option) => {
              const currentVal = ws.optionTeamMap[option] ?? (singleTeam ? singleTeam.id : 0);
              return (
                <div
                  key={option}
                  className="flex items-center gap-3 rounded border border-slate-200 bg-white px-3 py-2.5"
                >
                  <span className="text-sm font-medium text-slate-700 w-1/3 truncate">
                    {option}
                  </span>
                  <span className="text-slate-400 text-xs">→</span>
                  <div className="flex-1">
                    <Select
                      options={{ "": "— skip —", ...teamOptions }}
                      value={currentVal === 0 ? "" : currentVal.toString()}
                      onChange={(v) =>
                        setWizardState((prev) => ({
                          ...prev,
                          optionTeamMap: { ...prev.optionTeamMap, [option]: Number(v) }
                        }))
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {mappedCount > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              This will create{" "}
              <span className="font-semibold">
                {mappedCount} rule{mappedCount !== 1 ? "s" : ""}
              </span>{" "}
              — one per mapped option.
            </p>
          )}

          <p className="mt-2 text-xs text-slate-400">
            Need another team?{" "}
            <a
              href="/teams"
              className="underline text-primary hover:text-cornflower-blue-800"
              onClick={handleClose}
            >
              Create one →
            </a>{" "}
            — your progress is saved and you can resume when you return.
          </p>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="← Back" onClick={() => set("step", 0)} />
            <PrimaryBlueButton
              label={`Save ${mappedCount} Rule${mappedCount !== 1 ? "s" : ""}`}
              disabled={!canSaveSurveyRouting}
              onClick={saveSurveyRouting}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Business hours — Step 1: configure hours ────────────────────────────────

  function renderBusinessHoursStep0() {
    return (
      <>
        <ModalHeader title="Business hours routing — Step 1 of 2" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Set your business hours and the team that handles in-hours conversations.
          </p>

          <div className="flex flex-col gap-4">
            {/* In-hours team */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                During business hours, route to
              </label>
              <Select
                options={{ "": "— pick a team —", ...teamOptions }}
                value={ws.bhInHoursTeamId > 0 ? ws.bhInHoursTeamId.toString() : ""}
                onChange={(v) => set("bhInHoursTeamId", Number(v))}
              />
            </div>

            {/* Time range */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Hours
              </label>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-xs text-slate-500 mb-1">From</p>
                  <input
                    type="time"
                    value={ws.bhFromTime}
                    onChange={(e) => set("bhFromTime", e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300"
                  />
                </div>
                <span className="text-slate-400 mt-5">–</span>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 mb-1">To</p>
                  <input
                    type="time"
                    value={ws.bhToTime}
                    onChange={(e) => set("bhToTime", e.target.value)}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300"
                  />
                </div>
              </div>
            </div>

            {/* Days of week */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Days
              </label>
              <div className="flex gap-2 flex-wrap">
                {DAY_KEYS.map((day) => {
                  const active = ws.bhDays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleBhDay(day)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        active
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-slate-500 border-slate-300 hover:border-primary-hover"
                      }`}
                    >
                      {DAY_LABELS[day]}
                    </button>
                  );
                })}
              </div>
              {ws.bhDays.length === 0 && (
                <p className="mt-1 text-xs text-amber-600">Select at least one day.</p>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Back" onClick={() => set("scenario", null)} />
            <PrimaryBlueButton
              label="Next →"
              disabled={!canSaveBH}
              onClick={() => set("step", 1)}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Business hours — Step 2: after-hours team + confirm ─────────────────────

  function renderBusinessHoursStep1() {
    const inHoursTeam = teamList.find((t) => t.id === ws.bhInHoursTeamId);
    const afterHoursTeam = teamList.find((t) => t.id === ws.bhAfterHoursTeamId);
    const daysList = ws.bhDays
      .map((d) => DAY_LABELS[d])
      .join(", ");

    const rulesCount = ws.bhAfterHoursTeamId > 0 ? 2 : 1;

    return (
      <>
        <ModalHeader title="Business hours routing — Step 2 of 2" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Optionally pick a fallback team for after-hours visitors, then confirm.
          </p>

          <div className="flex flex-col gap-4">
            {/* After-hours team */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                After hours, route to
              </label>
              <Select
                options={{ "": "— no fallback (skip routing) —", ...teamOptions }}
                value={ws.bhAfterHoursTeamId > 0 ? ws.bhAfterHoursTeamId.toString() : ""}
                onChange={(v) => set("bhAfterHoursTeamId", Number(v))}
              />
              <p className="mt-1 text-xs text-slate-400">
                Skip this to handle after-hours visitors with a separate rule or workflow.
              </p>
            </div>

            {/* Summary */}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
              <p className="font-medium text-slate-700 mb-2">What will be created:</p>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="inline-block mt-0.5 w-5 h-5 rounded-full bg-primary-bg text-primary-active text-xs font-bold flex items-center justify-center shrink-0">1</span>
                  <div>
                    <p className="text-slate-700">
                      <span className="font-medium">{inHoursTeam?.name ?? "—"}</span>{" "}
                      receives chats between{" "}
                      <span className="font-medium">{ws.bhFromTime} – {ws.bhToTime}</span>{" "}
                      on <span className="font-medium">{daysList || "—"}</span>
                    </p>
                  </div>
                </div>
                {ws.bhAfterHoursTeamId > 0 && (
                  <div className="flex items-start gap-2">
                    <span className="inline-block mt-0.5 w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center shrink-0">2</span>
                    <p className="text-slate-700">
                      All other times →{" "}
                      <span className="font-medium">{afterHoursTeam?.name ?? "—"}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="← Back" onClick={() => set("step", 0)} />
            <PrimaryBlueButton
              label={`Save ${rulesCount} Rule${rulesCount !== 1 ? "s" : ""}`}
              onClick={saveBusinessHoursRouting}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Geographic routing — Step 1: map countries to teams ─────────────────────

  function renderGeographicStep0() {
    const countrySelectOptions = COUNTRIES.reduce(
      (acc, c) => { acc[c] = c; return acc; },
      {} as Record<string, string>
    );

    return (
      <>
        <ModalHeader title="Route by country" onClose={handleClose} />
        <ModalBody>
          <p className="text-sm text-slate-500 mb-4">
            Map each country to the team that handles visitors from that location.
            Rows you leave blank are skipped.
          </p>

          <div className="flex flex-col gap-2">
            {ws.geoRows.map((row, index) => (
              <div
                key={index}
                className="flex items-center gap-2 rounded border border-slate-200 bg-white px-3 py-2"
              >
                <div className="flex-1">
                  <Select
                    options={{ "": "— pick country —", ...countrySelectOptions }}
                    value={row.country}
                    onChange={(v) => updateGeoRow(index, { country: v })}
                  />
                </div>
                <span className="text-slate-400 text-xs shrink-0">→</span>
                <div className="flex-1">
                  <Select
                    options={{ "": "— pick team —", ...teamOptions }}
                    value={row.teamId > 0 ? row.teamId.toString() : ""}
                    onChange={(v) => updateGeoRow(index, { teamId: Number(v) })}
                  />
                </div>
                {ws.geoRows.length > 1 && (
                  <button
                    type="button"
                    className="text-slate-300 hover:text-red-400 transition-colors ml-1 shrink-0"
                    onClick={() => removeGeoRow(index)}
                    title="Remove row"
                    aria-label="Remove row"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            className="mt-3 text-sm text-primary-active hover:text-cornflower-blue-900 hover:underline flex items-center gap-1"
            onClick={addGeoRow}
          >
            <span className="text-base leading-none">+</span> Add country
          </button>

          {validGeoRows.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              This will create{" "}
              <span className="font-semibold">
                {validGeoRows.length} rule{validGeoRows.length !== 1 ? "s" : ""}
              </span>{" "}
              — one per mapped country.
            </p>
          )}

          <p className="mt-2 text-xs text-slate-400">
            Countries use GeoIP detection. Add a catch-all rule manually to handle
            visitors from unlisted countries.
          </p>
        </ModalBody>
        <ModalFooter>
          <div className="flex justify-between px-4 py-3 w-full">
            <SecondaryBlueButton label="Back" onClick={() => set("scenario", null)} />
            <PrimaryBlueButton
              label={`Save ${validGeoRows.length} Rule${validGeoRows.length !== 1 ? "s" : ""}`}
              disabled={!canSaveGeo}
              onClick={saveGeographicRouting}
            />
          </div>
        </ModalFooter>
      </>
    );
  }

  // ─── Scenario handler ─────────────────────────────────────────────────────────

  function handleScenarioSelect(id: Scenario) {
    if (id === "time-of-day") {
      clearWizardState();
      openManualEditor({
        trigger: "route-to-team",
        priority: 1,
        value: { teamId: defaultTeam?.id ?? 0 },
        expression: [
          [{ type: "timeOfDay", data: JSON.stringify({ from: "09:00", to: "17:00" }) }]
        ]
      });
      return;
    }
    if (id === "url") {
      clearWizardState();
      openManualEditor({
        trigger: "route-to-team",
        priority: 1,
        value: { teamId: defaultTeam?.id ?? 0 },
        expression: [
          [{ type: "url", data: JSON.stringify({ comparator: "contains", url: "" }) }]
        ]
      });
      return;
    }
    if (id === "language") {
      clearWizardState();
      openManualEditor({
        trigger: "route-to-team",
        priority: 1,
        value: { teamId: defaultTeam?.id ?? 0 },
        expression: [
          [{ type: "language", data: JSON.stringify({ comparator: "equals", language: "" }) }]
        ]
      });
      return;
    }
    set("scenario", id);
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (!ws.scenario) return <Modal maxWidth="max-w-lg" show>{renderPicker()}</Modal>;

  if (ws.scenario === "survey") {
    return (
      <Modal maxWidth="max-w-lg" show>
        {ws.step === 0 ? renderSurveyStep0() : renderSurveyStep1()}
      </Modal>
    );
  }

  if (ws.scenario === "business-hours") {
    return (
      <Modal maxWidth="max-w-lg" show>
        {ws.step === 0 ? renderBusinessHoursStep0() : renderBusinessHoursStep1()}
      </Modal>
    );
  }

  if (ws.scenario === "geographic") {
    return (
      <Modal maxWidth="max-w-lg" show>
        {renderGeographicStep0()}
      </Modal>
    );
  }

  if (ws.scenario === "unavailable-survey") {
    return (
      <Modal maxWidth="max-w-lg" show>
        {renderUnavailableSurvey()}
      </Modal>
    );
  }

  if (ws.scenario === "unavailable-bot") {
    return (
      <Modal maxWidth="max-w-lg" show>
        {renderUnavailableBot()}
      </Modal>
    );
  }

  if (ws.scenario === "overflow") {
    return (
      <Modal maxWidth="max-w-lg" show>
        {renderOverflowStep()}
      </Modal>
    );
  }

  return null;
}
